// Map an arbitrary caught value into a safe, user-facing error shape.
//
// Two distinct things flow into our error state across the app:
//   - Backend JSON payloads (when status !== 200, we do `res.json().then(setState{ error: res })`).
//     These are plain objects with intentional, user-facing fields.
//   - Runtime/network exceptions from fetch() / encryption / etc. Their
//     .message ("Failed to fetch", "NetworkError…", stack hints) can leak
//     internal info if shown to the user.
//
// We pass the first kind through and mask the second to a generic string.

const GENERIC_MESSAGE = "Something went wrong. Please try again.";

export const toUserError = (e) => {
  if (!e) return { message: GENERIC_MESSAGE };

  // Runtime/network exception — never echo .message to the UI.
  if (e instanceof Error) return { message: GENERIC_MESSAGE };

  if (typeof e === "string") return { message: GENERIC_MESSAGE };

  if (typeof e === "object") {
    const safe = {};
    // Prefer the i18n `message` from our own error envelope. Fall back to the
    // short reason phrase (`error`) that Spring's *default* error handler emits
    // when it bypasses GlobalExceptionHandler (e.g. AuthenticationEntryPoint's
    // sendError, which produces { timestamp, status, error, path } and NO
    // message). Only then mask to the generic string.
    safe.message =
      typeof e.message === "string" && e.message.trim() !== ""
        ? e.message
        : typeof e.error === "string" && e.error.trim() !== ""
        ? e.error
        : GENERIC_MESSAGE;
    if (typeof e.error === "string") safe.error = e.error;
    if (typeof e.status === "number") safe.status = e.status;
    if (e.successCode) safe.successCode = e.successCode;
    if (e.errorCode) safe.errorCode = e.errorCode;
    if (e.warning !== undefined) safe.warning = e.warning;
    if (Array.isArray(e.logs)) safe.logs = e.logs;
    return safe;
  }

  return { message: GENERIC_MESSAGE };
};
